import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage, LANGUAGES } from "@/hooks/useLanguage";
import { useTheme } from "@/hooks/useTheme";
import { Globe, Sun, Moon } from "lucide-react";
import { createPortal } from "react-dom";

// Language switcher + light/dark theme toggle.
export default function SettingsToggle() {
  const { language, setLanguage, t, isLanguageSwitching } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [langOpen, setLangOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);
  const [langPos, setLangPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (langOpen && langBtnRef.current) {
      const rect = langBtnRef.current.getBoundingClientRect();
      const width = 220;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setLangPos({ top: rect.bottom + 8, left });
    }
  }, [langOpen]);

  return (
    <div className="flex items-center gap-2" data-no-translate>
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="h-10 w-10 rounded-full"
        title={theme === "dark" ? t("common.light_mode", "Light mode") : t("common.dark_mode", "Dark mode")}
        aria-label={theme === "dark" ? t("common.light_mode", "Light mode") : t("common.dark_mode", "Dark mode")}
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
      <div className="relative">
        <Button
          ref={langBtnRef}
          variant="ghost"
          size="icon"
          onClick={() => setLangOpen(!langOpen)}
          disabled={isLanguageSwitching}
          className="h-10 w-10 rounded-full"
          title={t("common.language")}
          aria-label={t("common.language")}
          aria-haspopup="menu"
          aria-expanded={langOpen}
        >
          <Globe className="h-4 w-4" />
        </Button>

        {langOpen && createPortal(
          <>
            <div
              className="pointer-events-auto fixed inset-0 z-[9998]"
              onClick={() => setLangOpen(false)}
              aria-hidden="true"
            />
            <div
              role="menu"
              aria-label={t("common.language")}
              data-no-translate
              className="pointer-events-auto fixed z-[9999] w-[220px] max-h-[60vh] overflow-y-auto rounded-lg border border-border bg-popover shadow-xl py-1"
              style={{ top: langPos.top, left: langPos.left }}
            >
              {LANGUAGES.map((lang) => {
                const active = language === lang.code;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    disabled={isLanguageSwitching}
                    onClick={() => {
                      setLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-muted ${
                      active ? "text-primary font-medium" : "text-foreground"
                    }`}
                  >
                    <span>{lang.nativeName}</span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      {lang.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
      </div>
    </div>
  );
}
